import { Tab, Tabs } from '@mui/material';
import { PageContainer } from 'src/components/Page/PageContainer';
import { PageHeader } from 'src/components/Page/PageHeader';
import { PageScaffold } from 'src/components/Page/PageScaffold';
import { PageLayoutProps } from './page-layout.types';

/**
 * The scaffold + container + header primitives give granular control, but most
 * pages want the same default arrangement. This wrapper hides that wiring behind
 * a flat prop API so a new page is a single declarative block and every page
 * stays visually consistent without copy-pasting the composition. It lives at
 * the `Page/` root because it is the entry point most callers should reach for;
 * the primitives in the subfolders are the escape hatch.
 *
 * Composes PageScaffold (scroll + sticky header), PageContainer (width),
 * PageHeader (title block) and an optional controlled tab strip. Drop down to
 * the underlying primitives when you need finer control.
 */
export const PageLayout = <TabValue extends string = string>({
  title,
  subtitle,
  icon,
  actions,
  width = 'centered',
  tabs,
  children,
}: PageLayoutProps<TabValue>) => {
  const Container = width === 'fluid' ? PageContainer.Fluid : PageContainer.Centered;

  return (
    <PageScaffold
      header={
        <Container>
          <PageHeader title={title} subtitle={subtitle} icon={icon} actions={actions} />
          {/*
            A single tab carries no choice, so the strip is just noise — hide it
            and render the lone panel directly (children are unaffected).
          */}
          {tabs && tabs.items.length > 1 ? (
            <Tabs
              value={tabs.value}
              onChange={(_event, value) => tabs.onChange(value as TabValue)}
            >
              {tabs.items.map((item) => (
                <Tab
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  disabled={item.disabled}
                />
              ))}
            </Tabs>
          ) : null}
        </Container>
      }
    >
      <Container sx={{ pt: 3, pb: 6 }}>{children}</Container>
    </PageScaffold>
  );
};

PageLayout.displayName = 'PageLayout';
